import {useMemo, useState} from "react";
import {WellEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseWellDataTableFilterProps = Omit<UseDataTableFilterProps<WellEntity>, 'filterConfig'>

export function useWellDataTableFilter(props: UseWellDataTableFilterProps): UseDataTableFilterResult<WellEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [stageQuery, setStageQuery] = useState<string | undefined>(undefined);
  const [stageDebouncedTypeQuery] = useDebouncedValue(stageQuery, 200);

  const [siteQuery, setSiteQuery] = useState<string | undefined>(undefined);
  const [siteDebouncedTypeQuery] = useDebouncedValue(siteQuery, 200);

  const filterConfig: DataTableFilterConfig<WellEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "stage": {
        setQuery: setStageQuery,
        query: stageQuery,
        debouncedQuery: stageDebouncedTypeQuery,
        filter: (record, query) => !record.wellStageCode || !record.wellStageCode.toLowerCase().includes(query.toLowerCase())
      },
      "site": {
        setQuery: setSiteQuery,
        query: siteQuery,
        debouncedQuery: siteDebouncedTypeQuery,
        filter: (record, query) => !record.siteName || !record.siteName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    stageQuery,
    stageDebouncedTypeQuery,
    siteQuery,
    siteDebouncedTypeQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
